/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
} from "@mui/material";
import api from "../../api/index.js";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingSpinner } from "../loading_spinner";
import { AbilityContext } from "../../contexts/abilityContext";
import { DiscountCardExclusionTranslationFields } from "../discount_card_exclusions/translation_fields";
import { DiscountCardVariantTranslationFields } from "../discount_card_variant_groups/translation_fields";
import {
  buildExclusionDisclaimer,
  mergeExclusionTranslationItems,
} from "../discount_card_exclusions/utils";
import { strictCanScope } from "../../utils/caslStrict";
import {
  StyledDialogTitle,
  StyledFieldLabel,
  StyledReadOnlyField,
  StyledEditField,
  CrossIconContainer,
  StyledButton,
  StyledRestoreButton,
  FieldPair,
  scrollableContent,
  languageSelectorBox,
  fixedActionContainer,
  createStyledReadOnlyFieldProps,
} from "../translation_modal/styles";
import {
  createSpanishFieldProps,
  isEnglishValueBlank,
} from "../translation_modal/utils";

export const DiscountCardTranslationModal = ({
  open,
  onClose,
  discountCardData,
  restaurantGroupId,
  onSave,
  isLoading = false,
  onLoadingChange,
}) => {
  const { ability } = useContext(AbilityContext);
  const canManageTranslations = strictCanScope(ability, "update_discount_card", "Restaurant_Group", `${restaurantGroupId}`);
  const isReadOnly = !canManageTranslations;
  const { t } = useTranslation();

  const translateOr = (key, fallback, options) => t(key, { ...options, defaultValue: fallback });

  const [translations, setTranslations] = useState(null);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const previousCardId = useRef(null);
  const initialVariantTranslationsRef = useRef([]);

  const cloneVariantTranslations = (variantTranslations = []) =>
    variantTranslations.map((variant) => ({
      ...variant,
      exclusion_items: (variant.exclusion_items || []).map((item) => ({ ...item })),
      rules: (variant.rules || []).map((rule) => ({ ...rule })),
      spanish_exclusion_items: (variant.spanish_exclusion_items || []).map((item) => ({ ...item })),
      spanish_rules: (variant.spanish_rules || []).map((rule) => ({ ...rule })),
    }));

  const buildSpanishExclusionsValue = (englishItems, spanishItems, fallbackValue = "") =>
    buildExclusionDisclaimer(
      mergeExclusionTranslationItems(englishItems, spanishItems),
      fallbackValue
    );

  // Spanish text with no English source is cleared rather than re-saved.
  const buildExclusionItemsPayload = (spanishItems = [], englishItems = []) => {
    const englishItemsById = new Map(englishItems.map((item) => [item.id, item]));

    return spanishItems.map((item) => {
      const englishItem = englishItemsById.get(item.id);

      return {
        id: item.id,
        spanish_category: isEnglishValueBlank(englishItem?.category)
          ? null
          : item.spanish_category || null,
        spanish_description: isEnglishValueBlank(englishItem?.description)
          ? null
          : item.spanish_description || null,
      };
    });
  };

  const SPANISH_RULE_SOURCES = [
    ["spanish_category", "category"],
    ["spanish_applies_to", "applies_to"],
    ["spanish_number_of_guests", "number_of_guests"],
    ["spanish_discount", "discount"],
  ];

  const buildRulesPayload = (spanishRules = [], englishRules = []) => {
    const englishRulesById = new Map(englishRules.map((rule) => [rule.id, rule]));

    return spanishRules.map((spanishRule) => {
      const englishRule = englishRulesById.get(spanishRule.id);

      return SPANISH_RULE_SOURCES.reduce(
        (rule, [spanishField, englishField]) =>
          isEnglishValueBlank(englishRule?.[englishField])
            ? { ...rule, [spanishField]: "" }
            : rule,
        spanishRule,
      );
    });
  };

  const buildRulesTranslationItems = (rules = []) => {
    const englishRules = [];
    const spanishRules = [];

    rules.forEach((category) => {
      category?.perks?.forEach((perk) => {
        englishRules.push({
          id: perk.id,
          applies_to: perk.type || "",
          number_of_guests: perk.label || "",
          discount: perk.discount || "",
          category: category.category_name || "",
        });

        spanishRules.push({
          id: perk.id,
          spanish_applies_to: perk.spanish_translation?.translated_applies_to || "",
          spanish_number_of_guests: perk.spanish_translation?.translated_number_of_guests || "",
          spanish_discount: perk.spanish_translation?.translated_discount || "",
          spanish_category: perk.spanish_translation?.translated_category || "",
        });
      });
    });

    return { englishRules, spanishRules };
  };

  const buildVariantTranslations = (variants = []) =>
    variants.map((variant) => {
      const { englishRules, spanishRules } = buildRulesTranslationItems(variant?.rules || []);
      const exclusionItems = variant?.exclusion_items || [];
      const spanishExclusionItems = exclusionItems.map((item) => ({
          id: item.id,
          spanish_category:
            item?.spanish_translation?.translated_category || item?.spanish_category || "",
          spanish_description:
            item?.spanish_translation?.translated_description || item?.spanish_description || "",
      }));

      return {
        id: variant.id,
        name: variant?.name || "",
        exclusion_items: exclusionItems,
        rules: englishRules,
        spanish_exclusions:
          buildSpanishExclusionsValue(
            exclusionItems,
            spanishExclusionItems,
            variant?.spanish_translation?.translated_exclusions || "",
          ) || variant?.spanish_translation?.translated_exclusions || "",
        spanish_exclusion_items: spanishExclusionItems,
        spanish_rules: spanishRules,
      };
    });

  const buildVariantTranslationsPayload = (variantTranslations = []) =>
    variantTranslations.map((variant) => {
      const exclusionItems = buildExclusionItemsPayload(
        variant.spanish_exclusion_items,
        variant.exclusion_items,
      );

      return {
        id: variant.id,
        // Rebuilt from the sanitized items so the disclaimer matches what is saved.
        spanish_exclusions: buildSpanishExclusionsValue(
          variant.exclusion_items,
          exclusionItems,
          variant.spanish_exclusions,
        ),
        exclusion_items: exclusionItems,
        rules: buildRulesPayload(variant.spanish_rules, variant.rules),
      };
    });

  const normalizeDefaultVariantRules = (rules = []) =>
    rules.map((rule) => ({
      id: rule.id,
      spanish_applies_to:
        rule?.spanish_applies_to ??
        rule?.spanish_translation?.translated_applies_to ??
        "",
      spanish_number_of_guests:
        rule?.spanish_number_of_guests ??
        rule?.spanish_translation?.translated_number_of_guests ??
        "",
      spanish_discount:
        rule?.spanish_discount ??
        rule?.spanish_translation?.translated_discount ??
        "",
      spanish_category:
        rule?.spanish_category ??
        rule?.spanish_translation?.translated_category ??
        "",
    }));

  const normalizeDefaultVariantExclusionItems = (exclusionItems = []) =>
    exclusionItems.map((item) => ({
      id: item.id,
      spanish_category:
        item?.spanish_category ??
        item?.spanish_translation?.translated_category ??
        "",
      spanish_description:
        item?.spanish_description ??
        item?.spanish_translation?.translated_description ??
        "",
    }));

  const mergeDefaultVariantTranslations = (
    currentVariantTranslations = [],
    defaultVariantTranslations = [],
  ) => {
    if (!defaultVariantTranslations.length) {
      return cloneVariantTranslations(initialVariantTranslationsRef.current);
    }

    const defaultVariantsById = new Map(
      defaultVariantTranslations.map((variant) => [variant.id, variant]),
    );

    return currentVariantTranslations.map((variant) => {
      const defaultVariant = defaultVariantsById.get(variant.id);

      if (!defaultVariant) {
        return variant;
      }

      const nextSpanishRules = Array.isArray(defaultVariant.rules)
        ? normalizeDefaultVariantRules(defaultVariant.rules)
        : variant.spanish_rules;
      const nextSpanishExclusionItems = Array.isArray(defaultVariant.exclusion_items)
        ? normalizeDefaultVariantExclusionItems(defaultVariant.exclusion_items)
        : getVariantSpanishExclusionItems(variant);

      return {
        ...variant,
        spanish_rules: nextSpanishRules,
        spanish_exclusion_items: nextSpanishExclusionItems,
        spanish_exclusions: buildSpanishExclusionsValue(
          variant.exclusion_items,
          nextSpanishExclusionItems,
          defaultVariant.spanish_exclusions ?? variant.spanish_exclusions,
        ),
      };
    });
  };

  const resetTranslations = () => {
    setTranslations(null);
    previousCardId.current = null;
    initialVariantTranslationsRef.current = [];
  };

  useEffect(() => {
    if (!open || !discountCardData?.id) {
      return;
    }

    if (previousCardId.current !== discountCardData.id) {
      previousCardId.current = discountCardData.id;
      resetTranslations();
      loadTranslations();
    }
  }, [open, discountCardData?.id]);

  const loadTranslations = async () => {
    if (!discountCardData?.id) return;
    
    setIsLoadingData(true);
    onLoadingChange?.(true);
    
    try {
      const response = await api.get(`/v1/admin/discount_cards/${discountCardData.id}`);
      const data = response?.data;
      
      const { englishRules: allRules, spanishRules } = buildRulesTranslationItems(data?.rules || []);

      const exclusionItems = data?.exclusion_items || [];
      const spanishExclusionItems = exclusionItems.map((item) => ({
        id: item.id,
        spanish_category:
          item?.spanish_translation?.translated_category || "",
        spanish_description:
          item?.spanish_translation?.translated_description || "",
      }));
      
      const nextVariantTranslations = buildVariantTranslations(data?.discount_card_variants || []);
      initialVariantTranslationsRef.current = cloneVariantTranslations(nextVariantTranslations);

      setTranslations({
        name: data?.name || "",
        exclusions: data?.exclusions || "",
        exclusion_items: exclusionItems,
        rules: allRules,
        spanish_name: data?.spanish_translation?.translated_name || "",
        spanish_exclusions:
          buildSpanishExclusionsValue(
            exclusionItems,
            spanishExclusionItems,
            data?.spanish_translation?.translated_exclusions || ""
          ) || data?.spanish_translation?.translated_exclusions || "",
        spanish_exclusion_items: spanishExclusionItems,
        spanish_rules: spanishRules,
        discount_card_variants: nextVariantTranslations,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingData(false);
      onLoadingChange?.(false);
    }
  };

  const handleTranslationChange = (field, value) => {
    if (!translations) return;
    setTranslations({ ...translations, [field]: value });
  };

  const handleRuleTranslationChange = (ruleId, field, value) => {
    if (!translations) return;
    
    const updatedRules = [...translations.spanish_rules];
    const ruleIndex = updatedRules.findIndex((r) => r.id === ruleId);
    
    if (ruleIndex >= 0) {
      updatedRules[ruleIndex] = {
        ...updatedRules[ruleIndex],
        [field]: value,
      };
    } else {
      updatedRules.push({
        id: ruleId,
        [field]: value,
      });
    }
    
    setTranslations({ ...translations, spanish_rules: updatedRules });
  };

  const getRuleTranslation = (ruleId, field) => {
    if (!translations) return "";
    const rule = translations.spanish_rules.find((r) => r.id === ruleId);
    return rule?.[field] || "";
  };

  const handleExclusionTranslationChange = (exclusionId, field, value) => {
    if (!translations) return;

    const updatedExclusions = [...translations.spanish_exclusion_items];
    const exclusionIndex = updatedExclusions.findIndex(
      (item) => item.id === exclusionId
    );

    if (exclusionIndex >= 0) {
      updatedExclusions[exclusionIndex] = {
        ...updatedExclusions[exclusionIndex],
        [field]: value,
      };
    } else {
      updatedExclusions.push({
        id: exclusionId,
        [field]: value,
      });
    }

    setTranslations({
      ...translations,
      spanish_exclusion_items: updatedExclusions,
      spanish_exclusions: buildSpanishExclusionsValue(
        translations.exclusion_items,
        updatedExclusions,
        translations.spanish_exclusions
      ),
    });
  };

  const getExclusionTranslation = (exclusionId, field) => {
    if (!translations) return "";

    const exclusion = translations.spanish_exclusion_items.find(
      (item) => item.id === exclusionId
    );

    return exclusion?.[field] || "";
  };

  const getVariantSpanishExclusionItems = (variant) => {
    if (Array.isArray(variant?.spanish_exclusion_items) && variant.spanish_exclusion_items.length > 0) {
      return variant.spanish_exclusion_items;
    }

    return (variant?.exclusion_items || []).map((item) => ({
      id: item.id,
      spanish_category:
        item?.spanish_translation?.translated_category || item?.spanish_category || "",
      spanish_description:
        item?.spanish_translation?.translated_description || item?.spanish_description || "",
    }));
  };

  const getVariantById = (variantId) =>
    translations?.discount_card_variants.find((variant) => variant.id === variantId);

  const updateVariantTranslations = (variantId, updateVariant) => {
    setTranslations((currentTranslations) => {
      if (!currentTranslations) return currentTranslations;

      return {
        ...currentTranslations,
        discount_card_variants: currentTranslations.discount_card_variants.map((variant) =>
          variant.id === variantId ? updateVariant(variant) : variant,
        ),
      };
    });
  };

  const getVariantExclusionTranslation = (variantId, exclusionId, field) => {
    const currentVariant = getVariantById(variantId);
    if (!currentVariant) return "";

    const exclusion = getVariantSpanishExclusionItems(currentVariant).find(
      (item) => item.id === exclusionId,
    );

    return exclusion?.[field] || "";
  };

  const getVariantRuleTranslation = (variantId, ruleId, field) => {
    const currentVariant = getVariantById(variantId);
    if (!currentVariant) return "";

    const rule = currentVariant.spanish_rules.find((item) => item.id === ruleId);
    return rule?.[field] || "";
  };

  const handleVariantExclusionTranslationChange = (variantId, exclusionId, field, value) => {
    updateVariantTranslations(variantId, (currentVariant) => {
      const nextSpanishExclusionItems = [...getVariantSpanishExclusionItems(currentVariant)];
      const exclusionIndex = nextSpanishExclusionItems.findIndex((item) => item.id === exclusionId);

      if (exclusionIndex >= 0) {
        nextSpanishExclusionItems[exclusionIndex] = {
          ...nextSpanishExclusionItems[exclusionIndex],
          [field]: value,
        };
      } else {
        nextSpanishExclusionItems.push({ id: exclusionId, [field]: value });
      }

      return {
        ...currentVariant,
        spanish_exclusion_items: nextSpanishExclusionItems,
        spanish_exclusions: buildSpanishExclusionsValue(
          currentVariant.exclusion_items,
          nextSpanishExclusionItems,
          currentVariant.spanish_exclusions,
        ),
      };
    });
  };

  const handleVariantRuleTranslationChange = (variantId, ruleId, field, value) => {
    updateVariantTranslations(variantId, (currentVariant) => {
      const nextSpanishRules = [...currentVariant.spanish_rules];
      const ruleIndex = nextSpanishRules.findIndex((item) => item.id === ruleId);

      if (ruleIndex >= 0) {
        nextSpanishRules[ruleIndex] = {
          ...nextSpanishRules[ruleIndex],
          [field]: value,
        };
      } else {
        nextSpanishRules.push({ id: ruleId, [field]: value });
      }

      return { ...currentVariant, spanish_rules: nextSpanishRules };
    });
  };

  const handleSave = () => {
    if (!translations) return;

    onSave({
      ...discountCardData,
      spanish_name: isEnglishValueBlank(translations.name) ? "" : translations.spanish_name,
      exclusion_items: buildExclusionItemsPayload(
        translations.spanish_exclusion_items,
        translations.exclusion_items,
      ),
      rules: buildRulesPayload(translations.spanish_rules, translations.rules),
      discount_card_variants: buildVariantTranslationsPayload(translations.discount_card_variants),
    });
  };

  const handleRestoreDefaults = async () => {
    if (!discountCardData?.id || !translations) return;

    const currentTranslations = translations;
    setIsLoadingDefaults(true);
    try {
      const response = await api.get(
        `/v1/admin/discount_cards/${discountCardData.id}/fetch_default_translations`
      );
      const defaultTranslations = response?.data;
      const defaultExclusionItems = defaultTranslations?.exclusion_items || [];
      const nextSpanishExclusionItems = defaultExclusionItems.length
        ? defaultExclusionItems.map((item) => ({
            id: item.id,
            spanish_category: item?.spanish_category || "",
            spanish_description: item?.spanish_description || "",
          }))
        : translations.spanish_exclusion_items;

      setTranslations({
        ...currentTranslations,
        spanish_name: defaultTranslations?.spanish_name || "",
        spanish_exclusion_items: nextSpanishExclusionItems,
        spanish_exclusions: defaultExclusionItems.length
          ? buildSpanishExclusionsValue(
              currentTranslations.exclusion_items,
              nextSpanishExclusionItems,
              defaultTranslations?.spanish_exclusions || ""
            )
          : defaultTranslations?.spanish_exclusions || "",
        spanish_rules: defaultTranslations?.rules || [],
        discount_card_variants: mergeDefaultVariantTranslations(
          currentTranslations.discount_card_variants,
          defaultTranslations?.discount_card_variants || [],
        ),
      });
    } catch (error) {
      console.error("Failed to fetch default translations:", error);
      setTranslations(currentTranslations);
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  const handleClose = () => {
    resetTranslations();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "90vh",
          overflow: "hidden",
        },
      }}
    >
      {isLoadingData || !translations ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <LoadingSpinner isLoading={true} />
        </Box>
      ) : (
        <>
          <CrossIconContainer>
            <ClearIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
          </CrossIconContainer>

          <StyledDialogTitle>
            {`Manage Translations - ${discountCardData?.name || ""}`}
          </StyledDialogTitle>

          <DialogContent sx={scrollableContent}>
            <Box sx={languageSelectorBox}>
              <Typography sx={{ fontSize: "16px", fontWeight: 600, lineHeight: "24px", color: "#323232" }}>
                {translateOr("discount-cards.index.table.headers.Translate", "Spanish Translation")}
              </Typography>
            </Box>

            <Box sx={{ marginBottom: "16px" }}>
              <StyledFieldLabel>{translateOr("discount-cards.create.form.card-name", "Name")}</StyledFieldLabel>
              <FieldPair>
                <StyledReadOnlyField
                  fullWidth
                  value={translations.name}
                  disabled
                  variant="outlined"
                  size="small"
                  label="English"
                  {...createStyledReadOnlyFieldProps()}
                />
                <StyledEditField
                  fullWidth
                  value={translations.spanish_name}
                  onChange={(e) =>
                    handleTranslationChange(
                      "spanish_name",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  label="Español"
                  {...createSpanishFieldProps(translations.name, isReadOnly)}
                />
              </FieldPair>
            </Box>

            {translations.rules.length > 0 && (
              <Box sx={{ marginTop: "24px" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 600, lineHeight: "24px", color: "#323232", marginBottom: "16px" }}>
                  {translateOr("discount-cards.create.form.rules.rules", "Categories")}
                </Typography>

                {translations.rules.map((rule, index) => (
                  <Box key={rule.id} sx={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px", color: "#666" }}>
                      {translateOr(
                        "discount-cards.create.form.rules.heading",
                        "Category {{index}} - {{category}}",
                        {
                          index: index + 1,
                          category: rule.category,
                        },
                      )}
                    </Typography>

                    <Box sx={{ marginBottom: "12px" }}>
                      <StyledFieldLabel>{translateOr("discount-cards.create.form.rules.property", "Category")}</StyledFieldLabel>
                      <FieldPair>
                        <StyledReadOnlyField
                          fullWidth
                          value={rule.category}
                          disabled
                          variant="outlined"
                          size="small"
                          label="English"
                          {...createStyledReadOnlyFieldProps()}
                        />
                        <StyledEditField
                          fullWidth
                          value={getRuleTranslation(rule.id, "spanish_category")}
                          onChange={(e) =>
                            handleRuleTranslationChange(rule.id, "spanish_category", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          label="Español"
                          {...createSpanishFieldProps(rule.category, isReadOnly)}
                        />
                      </FieldPair>
                    </Box>

                    <Box sx={{ marginBottom: "12px" }}>
                      <StyledFieldLabel>{translateOr("discount-cards.create.form.rules.order-type", "Applies To")}</StyledFieldLabel>
                      <FieldPair>
                        <StyledReadOnlyField
                          fullWidth
                          value={rule.applies_to}
                          disabled
                          variant="outlined"
                          size="small"
                          label="English"
                          {...createStyledReadOnlyFieldProps()}
                        />
                        <StyledEditField
                          fullWidth
                          value={getRuleTranslation(rule.id, "spanish_applies_to")}
                          onChange={(e) =>
                            handleRuleTranslationChange(rule.id, "spanish_applies_to", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          label="Español"
                          {...createSpanishFieldProps(rule.applies_to, isReadOnly)}
                        />
                      </FieldPair>
                    </Box>

                    <Box sx={{ marginBottom: "12px" }}>
                      <StyledFieldLabel>{translateOr("discount-cards.create.form.rules.number-of-guests", "Number of Guests")}</StyledFieldLabel>
                      <FieldPair>
                        <StyledReadOnlyField
                          fullWidth
                          value={rule.number_of_guests}
                          disabled
                          variant="outlined"
                          size="small"
                          label="English"
                          {...createStyledReadOnlyFieldProps()}
                        />
                        <StyledEditField
                          fullWidth
                          value={getRuleTranslation(rule.id, "spanish_number_of_guests")}
                          onChange={(e) =>
                            handleRuleTranslationChange(rule.id, "spanish_number_of_guests", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          label="Español"
                          {...createSpanishFieldProps(rule.number_of_guests, isReadOnly)}
                        />
                      </FieldPair>
                    </Box>

                    <Box sx={{ marginBottom: "0px" }}>
                      <StyledFieldLabel>{translateOr("discount-cards.create.form.rules.discount-section", translateOr("discount-cards.create.form.rules.discounts", "Discount"))}</StyledFieldLabel>
                      <FieldPair>
                        <StyledReadOnlyField
                          fullWidth
                          value={rule.discount}
                          disabled
                          variant="outlined"
                          size="small"
                          label="English"
                          {...createStyledReadOnlyFieldProps()}
                        />
                        <StyledEditField
                          fullWidth
                          value={getRuleTranslation(rule.id, "spanish_discount")}
                          onChange={(e) =>
                            handleRuleTranslationChange(rule.id, "spanish_discount", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                          label="Español"
                          {...createSpanishFieldProps(rule.discount, isReadOnly)}
                        />
                      </FieldPair>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <DiscountCardExclusionTranslationFields
              descriptionLabel={translateOr(
                "discount-cards.create.form.exclusion-description",
                "Description"
              )}
              exclusions={translations.exclusion_items}
              getTranslation={getExclusionTranslation}
              isReadOnly={isReadOnly}
              onTranslationChange={handleExclusionTranslationChange}
              sectionTitle={translateOr(
                "discount-cards.create.form.exclusions",
                "Exclusions"
              )}
              translateOr={translateOr}
            />

            {translations.discount_card_variants.length > 0 && (
              <Box sx={{ marginTop: "16px" }}>
                {translations.discount_card_variants.map((variant, index) => (
                  <DiscountCardVariantTranslationFields
                    key={variant.id}
                    getExclusionTranslation={getVariantExclusionTranslation}
                    getRuleTranslation={getVariantRuleTranslation}
                    index={index}
                    isReadOnly={isReadOnly}
                    onExclusionTranslationChange={handleVariantExclusionTranslationChange}
                    onRuleTranslationChange={handleVariantRuleTranslationChange}
                    testIdPrefix={`discount-card-translation-variant-${variant.id}`}
                    translateOr={translateOr}
                    variant={variant}
                  />
                ))}
              </Box>
            )}
          </DialogContent>

          <Box sx={fixedActionContainer}>
            <StyledRestoreButton
              onClick={handleRestoreDefaults}
              variant="outlined"
              disabled={isLoading || isReadOnly || isLoadingDefaults}
            >
              {isLoadingDefaults ? <LoadingSpinner isLoading={true} /> : "Restore Defaults"}
            </StyledRestoreButton>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <StyledButton
                onClick={handleClose}
                variant="outlined"
                disabled={isLoading}
              >
                {t("confirmation-dialog.cancel") || "Cancel"}
              </StyledButton>
              <StyledButton
                onClick={handleSave}
                variant="contained"
                disabled={isLoading || isReadOnly}
              >
                {isLoading ? <LoadingSpinner isLoading={true} /> : t("confirmation-dialog.save") || "Save"}
              </StyledButton>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );
};
